import React, { useState, useEffect } from 'react';
import { CreditCard, X, ChevronDown, Loader2 } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface BillingProfile {
  id: number;
  name: string;
  company_name: string | null;
  is_default: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  workspaceId: number;
  workspaceName: string;
  currentBillingProfileId: number | null;
  onAssigned: () => void;
}

const AssignBillingProfileModal: React.FC<Props> = ({
  open,
  onClose,
  workspaceId,
  workspaceName,
  currentBillingProfileId,
  onAssigned,
}) => {
  const { showSuccess, showError } = useToast();
  const [profiles, setProfiles] = useState<BillingProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [saveHovered, setSaveHovered] = useState(false);

  useEffect(() => {
    if (open) {
      loadProfiles();
    }
  }, [open]);

  useEffect(() => {
    if (currentBillingProfileId) {
      setSelectedProfileId(currentBillingProfileId);
    }
  }, [currentBillingProfileId]);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/billing/profiles/names');
      setProfiles(response.data?.profiles || []);
    } catch (err: any) {
      showError(err.message || 'Failed to load billing accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedProfileId) return;

    try {
      setSaving(true);
      await apiService.assignBillingProfile(workspaceId, selectedProfileId);
      showSuccess('Billing account assigned');
      onAssigned();
      onClose();
    } catch (err: any) {
      showError(err.message || 'Failed to assign billing account');
    } finally {
      setSaving(false);
    }
  };

  const currentProfile = profiles.find((p) => p.id === currentBillingProfileId);
  const selectedProfile = profiles.find((p) => p.id === selectedProfileId);
  const hasChanged = selectedProfileId !== currentBillingProfileId;
  const canSave = !saving && selectedProfileId && profiles.length > 0 && hasChanged;

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--v2-border)',
          maxWidth: '480px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-sans)',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (dropdownOpen) setDropdownOpen(false);
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--v2-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-primary-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CreditCard size={20} strokeWidth={1.5} color="var(--v2-primary)" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--v2-text)',
                  margin: 0,
                  letterSpacing: '-0.025em',
                }}
              >
                Billing Account
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '2px 0 0 0' }}>
                {workspaceName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--v2-text-muted)',
              transition: 'all 0.15s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
              e.currentTarget.style.color = 'var(--v2-text)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--v2-text-muted)';
            }}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Current Assignment */}
          {currentProfile && (
            <div
              style={{
                backgroundColor: 'var(--v2-surface-alt)',
                border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-md)',
                padding: '14px 16px',
                marginBottom: '24px',
              }}
            >
              <p
                style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: 'var(--v2-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  margin: '0 0 6px 0',
                }}
              >
                Currently Assigned
              </p>
              <p style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
                {currentProfile.name}
                {currentProfile.company_name &&
                  currentProfile.company_name !== currentProfile.name &&
                  ` — ${currentProfile.company_name}`}
              </p>
            </div>
          )}

          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                gap: '12px',
              }}
            >
              <Loader2
                size={24}
                strokeWidth={2}
                color="var(--v2-primary)"
                style={{ animation: 'spin 1s linear infinite' }}
              />
              <span style={{ color: 'var(--v2-text-tertiary)', fontSize: '0.875rem' }}>Loading...</span>
            </div>
          ) : profiles.length === 0 ? (
            <div
              style={{
                backgroundColor: 'var(--v2-warning-bg)',
                border: '1px solid #fcd34d',
                borderRadius: 'var(--radius-md)',
                padding: '14px 16px',
              }}
            >
              <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#92400e', margin: 0 }}>
                No billing accounts found. Please create a billing account first.
              </p>
            </div>
          ) : (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: 'var(--v2-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '8px',
                }}
              >
                Select Billing Account
              </label>

              {/* Custom Select Dropdown */}
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(!dropdownOpen);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    backgroundColor: 'var(--v2-surface)',
                    border: `1px solid ${dropdownOpen ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem',
                    color: selectedProfile ? 'var(--v2-text)' : 'var(--v2-text-muted)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: dropdownOpen ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                  }}
                >
                  <span>
                    {selectedProfile ? (
                      <>
                        {selectedProfile.name}
                        {selectedProfile.company_name && selectedProfile.company_name !== selectedProfile.name && (
                          <span style={{ color: 'var(--v2-text-tertiary)' }}> ({selectedProfile.company_name})</span>
                        )}
                      </>
                    ) : (
                      'Choose an account...'
                    )}
                  </span>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.5}
                    color="var(--v2-text-muted)"
                    style={{
                      transition: 'transform 0.15s ease',
                      transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                    }}
                  />
                </button>

                {dropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: 'var(--v2-surface)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-lg)',
                      zIndex: 10,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      padding: '4px',
                    }}
                  >
                    {profiles.map((profile) => (
                      <button
                        key={profile.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProfileId(profile.id);
                          setDropdownOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor:
                            selectedProfileId === profile.id ? 'var(--v2-primary-light)' : 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          borderRadius: 'var(--radius-sm)',
                          transition: 'background-color 0.1s',
                        }}
                        onMouseOver={(e) => {
                          if (selectedProfileId !== profile.id) {
                            e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (selectedProfileId !== profile.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <span style={{ fontWeight: 500, color: 'var(--v2-text)', fontSize: '0.875rem' }}>
                          {profile.name}
                        </span>
                        {profile.company_name && profile.company_name !== profile.name && (
                          <span style={{ color: 'var(--v2-text-tertiary)', fontSize: '0.875rem' }}>
                            ({profile.company_name})
                          </span>
                        )}
                        {profile.is_default && (
                          <span
                            style={{
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              color: 'var(--v2-success)',
                              backgroundColor: 'var(--v2-success-bg)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                          >
                            Default
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '8px 0 0 0' }}>
                This account will be used for all billing related to this workspace.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--v2-border)',
            backgroundColor: 'var(--v2-surface-alt)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <button
            onClick={onClose}
            onMouseEnter={() => setCancelHovered(true)}
            onMouseLeave={() => setCancelHovered(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: cancelHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
              color: cancelHovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!canSave}
            onMouseEnter={() => setSaveHovered(true)}
            onMouseLeave={() => setSaveHovered(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: !canSave ? 'var(--v2-border)' : saveHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
              color: !canSave ? 'var(--v2-text-muted)' : '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: !canSave ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s',
              minWidth: '120px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            {saving && <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
            {saving ? 'Saving...' : 'Assign Account'}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AssignBillingProfileModal;
